import React, { useState, useEffect, useContext } from 'react';
import {
  GlobalContext,
  OPEN_WIDGET,
} from '@mummssoftware/global-context/GlobalContext';
import { VFGlobalContext } from '@mummssoftware/global-context';
import { SET_ORDERS_MODE } from '@mummssoftware/global-context/VFGlobalContext';
import { CheckCPFOrder } from '@mummssoftware/common-ui';
import {
  formatDateMMDDYY,
  formatDateTimeMMDDpm,
} from '@mummssoftware/utils/formatters';
import { isSameDate, isSameTime } from '@mummssoftware/utils/utils';
import {
  Hospice,
  PSO,
  CPF,
  CarePlanFrequency,
  error,
  success,
} from '@mummssoftware/utils/constants';
import {
  OrdersGlobalContext,
  SET_DISPLAY_CONFIRMATION,
  SET_ORDER_VERIFIED,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import { offsetDate, toSiteTz } from '@mummssoftware/utils/orders/orders-utils';
import {
  mapOrderName,
  FOR_CAUSE,
  LEFT_GEOGRAPHIC_AREA,
  NON_CERTIFIED,
  DEATH,
  REVOKED,
  TRANSFERRED,
  ADM,
  LOC,
  FC,
  OTH,
  MMD,
} from '@mummssoftware/utils/orders';
import moment from 'moment-timezone';
import CheckOrderButtons from './CheckOrderButtons';
import {
  VerbalCheck,
  EffDateCheck,
  EffDateTimeCheck,
  PhysicianCheck,
  LOCCheck,
  FacilityCheck,
  DischargeCheck,
  OrderType,
} from './components';
import { isDisType } from '../../Statements/utils';
import { ProgressNotesContext } from '../../Context/ProgressNotesContext';
import { Context } from '../../../context';
import {
  CheckMenuWrapper,
  CheckOrderInfo,
} from '../../../components/layout/WrapperDiv';

type CheckOrderMenuProps = {
  onOrderVerified: (event: any) => void;
  onCancelClick: () => void;
};

const CheckOrderMenu = ({
  onOrderVerified,
  onCancelClick,
}: CheckOrderMenuProps) => {
  const {
    LOCtype,
    effectiveDate,
    facilityType,
    dischargeReason,
    locTypes,
    dischargeTypes,
    facilityOptions,
    admitPhysicianList,
    disOrderMap,
    dispatch,
    isPhysicianRoleGroup,
  } = useContext(ProgressNotesContext);
  const {
    orderType,
    admitPhysician,
    verbalDate,
    orderVerified,
    dispatchOrders,
  } = useContext(OrdersGlobalContext);

  const { patient, currentUser } = useContext(Context);
  const { doctorsGlobal, dispatch: dispatchGlobal } = useContext(GlobalContext);
  const { VForderData, dispatchVF } = useContext(VFGlobalContext);
  const isAdmitType = orderType === ADM;
  const isHospiceProgram = patient?.programSiteName === Hospice;

  const tz = moment.tz.guess();
  const siteTimezone = (currentUser && currentUser.siteTimezone) || tz;

  // check against verbalDate
  const [checkVerbalDate, setVerbalDate] = useState<Date | null>(null);
  const [verbalDateSeverity, setVerbalDateSeverity] = useState<mumms.Severity>(
    null,
  );

  // check against effectiveDate
  const [checkEffDate, setEffDate] = useState<Date | null>(null);
  const siteEffDate = checkEffDate && toSiteTz(checkEffDate, siteTimezone);
  const [effDateSeverity, setEffDateSeverity] = useState<mumms.Severity>(null);

  // check against admitPhysician
  const [checkDoctor, setDoctor] = useState<
    mumms.Doctor | undefined
    // check against LOCtype
  >(undefined);
  const [doctorSeverity, setDocSeverity] = useState<mumms.Severity>(null);

  const [checkLocType, setLocType] = useState<mumms.OptionType | undefined>(
    undefined,
  );
  const [locSeverity, setLocSeverity] = useState<mumms.Severity>(null);

  // check against facilityType
  const [checkFacility, setFacility] = useState<mumms.OptionType | undefined>(
    undefined,
  );
  const [facilitySeverity, setFacSeverity] = useState<mumms.Severity>(null);

  // check against dischargeReason
  const [checkDisReason, setDischargeReason] = useState<
    mumms.OptionType | undefined
  >(undefined);
  const [dischargeSeverity, setDisSeverity] = useState<mumms.Severity>(null);

  const handleVerbalDateChange = (verbaldate: any) => {
    setVerbalDate(verbaldate);
    handleVerbalDateSeverity(verbaldate);
  };
  const handleVerbalDateSeverity = (verbaldate: any) => {
    if (verbaldate && verbalDate && isSameDate(verbaldate, verbalDate)) {
      setVerbalDateSeverity(success);
    } else {
      setVerbalDateSeverity(error);
    }
  };

  const handleEffDateChange = (effdate: any) => {
    const newDate = offsetDate(effdate);
    setEffDate(newDate);
    const isDISOrder =
      orderType &&
      isDisType(orderType) &&
      disOrderMap &&
      disOrderMap[orderType];
    if (isAdmitType || isDISOrder) {
      handleEffDateTimeSeverity(newDate);
    } else {
      handleEffDateSeverity(newDate);
    }
  };
  const handleEffDateSeverity = (effdate: any) => {
    if (effdate && effectiveDate && isSameDate(effdate, effectiveDate)) {
      setEffDateSeverity(success);
    } else {
      setEffDateSeverity(error);
    }
  };
  const handleEffDateTimeSeverity = (effdate: any) => {
    if (effdate && effectiveDate && isSameTime(effdate, effectiveDate)) {
      setEffDateSeverity(success);
    } else {
      setEffDateSeverity(error);
    }
  };

  const [physMenuOpen, setPhysMenuOpen] = useState(false);
  const [locMenuOpen, setLOCMenuOpen] = useState(false);
  const [facMenuOpen, setFacMenuOpen] = useState(false);

  const onPhysFocus = () => {
    setPhysMenuOpen(true);
  };
  const onLocFocus = () => {
    setLOCMenuOpen(true);
  };
  const onFacFocus = () => {
    setFacMenuOpen(true);
  };

  const handlePhysicianChange = (physician?: any) => {
    setDoctor(physician);
    handlePhysicianSeverity(physician);
    setPhysMenuOpen(false);
  };
  const handlePhysicianSeverity = (physician?: any) => {
    // display admitPhysician.label if error
    if (
      physician &&
      admitPhysician &&
      physician.personNumber === admitPhysician.personNumber
    ) {
      setDocSeverity(success);
    } else {
      setDocSeverity(error);
    }
  };

  const handleLocTypeChange = (loc?: any) => {
    setLocType(loc);
    handleLocTypeSeverity(loc);
    setLOCMenuOpen(false);
  };

  const handleLocTypeSeverity = (loc?: any) => {
    // display LOCtype if error
    if (loc && LOCtype && loc.id === LOCtype) {
      setLocSeverity(success);
    } else {
      setLocSeverity(error);
    }
  };

  const handleFacilityChange = (fac?: any) => {
    setFacility(fac);
    handleFacilitySeverity(fac);
    setFacMenuOpen(false);
  };

  const handleFacilitySeverity = (fac?: any) => {
    // display facilityType.name if error
    if (fac && facilityType && fac.id === facilityType.id) {
      setFacSeverity(success);
    } else {
      setFacSeverity(error);
    }
  };

  const handleDischargeChange = (disreason: any) => {
    setDischargeReason(disreason);
    handleDischargeSeverity(disreason);
  };
  const handleDischargeSeverity = (disreason: any) => {
    // display dischargeReason.label if error
    if (disreason && dischargeReason && disreason.id === dischargeReason.id) {
      setDisSeverity(success);
    } else {
      setDisSeverity(error);
    }
  };

  const getEffDateTimeComponent = () => (
    <EffDateTimeCheck
      checkEffDate={siteEffDate}
      handleEffDateChange={handleEffDateChange}
      severity={effDateSeverity}
      error={
        effDateSeverity === error && effectiveDate
          ? formatDateTimeMMDDpm(effectiveDate)
          : undefined
      }
    />
  );
  const getEffDateComponent = () => (
    <EffDateCheck
      checkEffDate={siteEffDate}
      handleEffDateChange={handleEffDateChange}
      severity={effDateSeverity}
      error={
        effDateSeverity === error && effectiveDate
          ? formatDateMMDDYY(effectiveDate)
          : undefined
      }
    />
  );
  const getLOCComponent = () => (
    <LOCCheck
      checkLocType={checkLocType}
      locOptions={locTypes || []}
      handleLocTypeChange={handleLocTypeChange}
      severity={locSeverity}
      error={locSeverity === error && LOCtype ? LOCtype : undefined}
      onFocus={onLocFocus}
      menuIsOpen={locMenuOpen}
      openMenuOnClick
      openMenuOnFocus
    />
  );

  const getFacilityComponent = () => (
    <FacilityCheck
      checkFacility={checkFacility}
      facilityOptions={facilityOptions}
      handleFacilityChange={handleFacilityChange}
      severity={facilitySeverity}
      error={
        facilitySeverity === error && facilityType
          ? facilityType.label
          : undefined
      }
      onFocus={onFacFocus}
      menuIsOpen={facMenuOpen}
      openMenuOnClick
      openMenuOnFocus
    />
  );

  const getFCComponents = () => (
    <>
      {getFacilityComponent()}
      {getEffDateComponent()}
    </>
  );

  const getLocComponents = () => (
    <>
      {getLOCComponent()}
      {getFCComponents()}
    </>
  );
  const getAdmitComponents = () => (
    <>
      {getEffDateTimeComponent()}
      {getLOCComponent()}
      {getFacilityComponent()}
    </>
  );

  const getFrequencyOrderComponents = () => (
    <>
      <CheckCPFOrder VFOrderData={VForderData} />
    </>
  );

  /**
   * ORDER READ BACK AND VERIFIED VERIFICATION
   */

  const getApprovalVerification = () =>
    isPhysicianRoleGroup && orderType !== ADM
      ? true
      : getApprovalNonPhysVerification();
  const getApprovalNonPhysVerification = () =>
    doctorSeverity === success && verbalDateSeverity === success;
  const getEffDateVerification = () =>
    effDateSeverity === success && getApprovalVerification();
  const getFreqVerification = () =>
    Object.values(VForderData).length > 0 && getApprovalVerification();
  const getFCVerification = () =>
    facilitySeverity === success && getEffDateVerification();
  const getLOCVerification = () =>
    locSeverity === success && getFCVerification();
  const getLocFCVerification = () =>
    locSeverity === success && facilitySeverity === success;
  const getDischargeVerification = () =>
    dischargeSeverity === success && getEffDateVerification();
  const getAdmitVerification = () =>
    getEffDateVerification() &&
    getApprovalVerification() &&
    getLocFCVerification();

  const getOrderVerification = (
    ordertype: mumms.DischargeEntityType | mumms.EntityOrderType | null,
  ) => {
    switch (ordertype) {
      case FOR_CAUSE:
      case REVOKED:
        return getDischargeVerification();
      case DEATH:
      case TRANSFERRED:
      case NON_CERTIFIED:
      case LEFT_GEOGRAPHIC_AREA:
        return getEffDateVerification();
      case FC:
        return getFCVerification();
      case LOC:
        return getLOCVerification();
      case CPF:
        return getFreqVerification();
      case ADM:
        return isHospiceProgram
          ? getAdmitVerification()
          : getEffDateVerification();
      case PSO:
      case OTH:
      case MMD:
        return getApprovalVerification();
      default:
        return getApprovalVerification();
    }
  };

  useEffect(() => {
    const verification = getOrderVerification(orderType);
    if (verification) {
      dispatchOrders({
        type: SET_ORDER_VERIFIED,
        payload: true,
      });
    } else {
      dispatchOrders({
        type: SET_ORDER_VERIFIED,
        payload: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    doctorSeverity,
    verbalDateSeverity,
    effDateSeverity,
    facilitySeverity,
    locSeverity,
    dischargeSeverity,
  ]);

  const getCheckComponents = (
    ordertype: mumms.DischargeEntityType | mumms.EntityOrderType | null,
  ) => {
    switch (ordertype) {
      case FOR_CAUSE:
      case REVOKED:
        return (
          <>
            <DischargeCheck
              checkDisReason={checkDisReason}
              dischargeOptions={
                orderType && dischargeTypes
                  ? (dischargeTypes as any)[orderType]
                  : []
              }
              handleDischargeChange={handleDischargeChange}
              severity={dischargeSeverity}
              error={
                dischargeSeverity === error && dischargeReason
                  ? dischargeReason.label
                  : undefined
              }
            />
            {getEffDateTimeComponent()}
          </>
        );
      case NON_CERTIFIED:
      case LEFT_GEOGRAPHIC_AREA:
      case DEATH:
      case TRANSFERRED:
        return getEffDateTimeComponent();
      case FC:
        return getFCComponents();
      case LOC:
        return getLocComponents();
      case CPF:
        return getFrequencyOrderComponents();
      case ADM:
        return isHospiceProgram
          ? getAdmitComponents()
          : getEffDateTimeComponent();
      default:
        return <></>;
    }
  };

  const doctorList = isAdmitType ? admitPhysicianList : doctorsGlobal;

  const onModifyClick = () => {
    dispatchGlobal({
      type: OPEN_WIDGET,
      payload: { componentName: CarePlanFrequency },
    });
    dispatchOrders({ type: SET_DISPLAY_CONFIRMATION, payload: false });
    dispatchVF({ type: SET_ORDERS_MODE, payload: true });
  };
  const getApprovalComponents = () =>
    isPhysicianRoleGroup && orderType !== ADM ? (
      <></>
    ) : (
      <>
        <OrderType orderName={(mapOrderName as any)[orderType as any]} />
        <VerbalCheck
          verbalDate={checkVerbalDate}
          handleVerbalDateChange={handleVerbalDateChange}
          severity={verbalDateSeverity}
          error={
            verbalDateSeverity === error && verbalDate
              ? formatDateMMDDYY(verbalDate)
              : undefined
          }
        />
        <PhysicianCheck
          checkDoctor={checkDoctor as mumms.Doctor}
          doctorsOptions={doctorList as mumms.Doctor[]}
          handlePhysicianChange={handlePhysicianChange}
          severity={doctorSeverity}
          onFocus={onPhysFocus}
          menuIsOpen={physMenuOpen}
          openMenuOnClick
          openMenuOnFocus
          error={
            doctorSeverity === error && admitPhysician
              ? admitPhysician.label
              : undefined
          }
        />
      </>
    );

  return (
    <CheckMenuWrapper>
      <CheckOrderButtons
        onOrderVerified={onOrderVerified}
        onCancelClick={onCancelClick}
        orderVerified={orderVerified}
        onModifyClick={onModifyClick}
        orderType={orderType}
      />

      <CheckOrderInfo id="information-verification">
        {getApprovalComponents()}
        {getCheckComponents(orderType)}
      </CheckOrderInfo>
    </CheckMenuWrapper>
  );
};
export default CheckOrderMenu;
